import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import supabase from '../lib/supabaseClient';
// ONLY ADD THESE TWO IMPORTS - everything else stays the same
import { ROLE_PERMISSIONS, hasPermission as checkPermission, getRolePermissions } from '../lib/permissions';
import { PLAN_FEATURES, hasFeature as checkFeature, getFeatureValue, checkLimit, PLAN_METADATA } from '../lib/plans';

const AuthContext = createContext();

// Tenant-level account state hung on the `user` object. Both load paths (the
// background refresh and the profile path) select the same columns so the banners
// in Layout never depend on which path resolved first.
const TENANT_ACCOUNT_FIELDS =
  'plan, subscription_status, messaging_enabled, messaging_disabled_reason, ' +
  'current_month_sms_count, topup_messages_current_cycle, plan_limits, admin_banner, ' +
  'is_reviewer, reviewer_expires_at, reviewer_kind';

// Usage percentage against the allotment, mirroring supabase/functions/_shared/smsLimit.ts.
// Kept in sync by hand: that file is Deno TS and cannot be imported here. If the
// limit math changes there, change it here too.
const usagePctFromTenant = (tenant) => {
  const limits = tenant?.plan_limits || {};
  const base = limits.sms_limit ?? limits.monthly_sms_limit ?? null;
  if (base == null) return null;
  const total = base + (tenant?.topup_messages_current_cycle || 0);
  if (!total || total <= 0) return null;
  return Math.round(((tenant?.current_month_sms_count || 0) / total) * 1000) / 10;
};

const accountStateFromTenant = (tenant) => {
  const limits = tenant?.plan_limits || {};
  const base = limits.sms_limit ?? limits.monthly_sms_limit ?? null;
  return {
    subscription_status: tenant?.subscription_status || null,
    messaging_enabled: tenant?.messaging_enabled !== false,
    messaging_disabled_reason: tenant?.messaging_disabled_reason || null,
    sms_count: tenant?.current_month_sms_count || 0,
    sms_limit_total: base == null ? null : base + (tenant?.topup_messages_current_cycle || 0),
    sms_usage_pct: usagePctFromTenant(tenant),
    // Free-form notice addressed to ONE tenant, set by hand on tenants.admin_banner.
    // Deliberately NOT the messaging kill switch: that column gates every outbound
    // send (supabase/functions/_shared/messagingGate.ts), so using it to say
    // something would silently stop the tenant's messaging. Null = no banner.
    admin_banner: tenant?.admin_banner || null,
    // Review sandbox (tenants.is_reviewer): an outside evaluator's locked-down account. Layout shows a
    // "sample data" banner and stops showing the "account paused" notice (messaging is off by design).
    is_reviewer: tenant?.is_reviewer === true,
    // Prospect demo (tenants.reviewer_kind = 'prospect_demo'): a reviewer account built from a visitor's own website. Same lock-down,
    // different wording (Layout) and no reviewer intro video.
    is_prospect_demo: tenant?.reviewer_kind === 'prospect_demo',
    reviewer_expires_at: tenant?.reviewer_expires_at || null
  };
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const userIdRef = useRef(null);

  // Live-push ToS re-arms into any already-open session. Without this, a
  // tenant with the app already loaded would keep working with a stale
  // `terms_agreed_at`/`terms_notice_message` until they happened to reload —
  // exactly what send-tos-alert's own copy promises NOT to do ("blocks...
  // immediately, for everyone there"). Subscribes only to this user's own
  // row, same postgres_changes pattern WebChatInbox.jsx already uses.
  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel(`tos-gate-${user.id}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'users_profile',
          filter: `id=eq.${user.id}` }, (payload) => {
        const row = payload.new;
        setUser(prev => prev && ({
          ...prev,
          terms_agreed_at: row.terms_agreed_at ?? null,
          terms_notice_message: row.terms_notice_message ?? null,
          responsible_use_agreed_at: row.responsible_use_agreed_at ?? null,
        }));
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id]);

  useEffect(() => {
    let mounted = true;

    // Capture whether the page loaded with auth tokens in the URL hash (magic link,
    // email confirmation, invitation). Must be read synchronously before Supabase
    // clears the hash. Used below to gate audit logging in the INITIAL_SESSION handler.
    const hasAuthTokensOnLoad = window.location.hash.includes('access_token=') &&
      window.location.hash.includes('refresh_token=');

   const initializeAuth = async () => {
  try {
    console.log('🔐 Initializing auth...');
    console.log('🔐 URL:', window.location.href);
    console.log('🔐 Has query params:', window.location.search);

    // Clean custom OAuth hash BEFORE Supabase processes it
const search = window.location.search;
if (search.includes('calendar_connected=1')) {
  console.log('🧹 Cleaning calendar OAuth query params before auth init');
  window.history.replaceState({}, document.title, window.location.pathname);
}

    // Check if we have auth tokens in the URL hash (magic link, password reset, etc.)
    const hash = window.location.hash;
    const hasAuthTokens = hash.includes('access_token=') && hash.includes('refresh_token=');

    // If tokens in hash, wait longer for Supabase and onAuthStateChange handler to complete
    if (hasAuthTokens) {
      console.log('🔗 Auth tokens found in URL hash - waiting for Supabase to process...');
      await new Promise(resolve => setTimeout(resolve, 3000));
    }

    // Normal flow - get existing session
    let { data: { session }, error } = await supabase.auth.getSession();

    console.log('🔐 Initial getSession result:', {
      hasSession: !!session,
      hasError: !!error,
      userId: session?.user?.id,
      hasTokensInHash: hasAuthTokens
    });

    // If no session found and NO tokens in hash, check if we have a refresh token before attempting refresh
    // (Skip refresh when tokens are in hash - Supabase will process them via onAuthStateChange)
    if (!session && !error && !hasAuthTokens) {
      // Check if there's actually a refresh token in storage before attempting refresh
      const storedSession = localStorage.getItem('supabase.auth.token');
      const hasRefreshToken = storedSession && storedSession.includes('refresh_token');

      if (hasRefreshToken) {
        console.log('🔄 No session found but refresh token exists, attempting refresh...');
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();

        if (refreshData?.session) {
          console.log('✅ Refresh succeeded!');
          session = refreshData.session;
          error = null;
        } else {
          console.log('❌ Refresh failed:', refreshError?.message);
          // Don't treat this as a fatal error - just means user needs to log in
          error = null;
        }
      } else {
        console.log('ℹ️ No session and no refresh token - user is logged out');
      }
    }

    if (error) {
      console.error('❌ Session error:', error);
      if (mounted) {
        setUser(null);
        setSession(null);
        setLoading(false);
      }
      return;
    }
    
    if (session?.user && mounted) {
      console.log('✅ Session found for:', session.user.email);
      console.log('🔑 Session expires at:', new Date(session.expires_at * 1000).toLocaleString());

      setSession(session);

      if (session.access_token) {
        localStorage.setItem('auth_token', session.access_token);
      }

      // Audit log: session restore for returning users with a persistent session.
      // These logins are invisible to /api/auth/login (which only fires when the form
      // is submitted). Use sessionStorage so we log at most once per browser tab session
      // (cleared when the tab closes), not on every page refresh.
      const sessionAuditKey = `session_audited_${session.user.id}`;
      if (!sessionStorage.getItem(sessionAuditKey) && session.access_token) {
        sessionStorage.setItem(sessionAuditKey, 'true');
        fetch('/api/auth/log-session-start', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ source: 'session_restore' }),
        }).catch(() => {}); // Non-blocking — auth flow must not depend on audit logging
      }

      await loadUserInfo(session.user);
      
    } else {
      console.log('❌ No session found after refresh attempt');
      if (mounted) {
        setUser(null);
        setSession(null);
        setLoading(false);
        console.log('✅ Loading set to false - no session');
      }
    }
    
  } catch (error) {
    console.error('❌ Error initializing auth:', error);
    if (mounted) {
      setUser(null);
      setSession(null);
      setLoading(false);
    }
  }
};

    // Function to load user info from database
    const loadUserInfo = async (authUser) => {
      try {
        console.log('📊 Loading user profile for:', authUser.email);
        
        // Try to get role and tenant_id from BOTH app_metadata AND user_metadata
        let role = authUser.user_metadata?.role || authUser.app_metadata?.role;
        let tenant_id = authUser.user_metadata?.tenant_id || authUser.app_metadata?.tenant_id;

        console.log('🔍 Raw authUser.user_metadata:', authUser.user_metadata);
        console.log('🔍 Raw authUser.app_metadata:', authUser.app_metadata);
        
        console.log('🔍 Auth app_metadata - role:', authUser.app_metadata?.role, 'tenant_id:', authUser.app_metadata?.tenant_id);
        console.log('🔍 Auth user_metadata - role:', authUser.user_metadata?.role, 'tenant_id:', authUser.user_metadata?.tenant_id);
        console.log('🔍 Final metadata values - role:', role, 'tenant_id:', tenant_id);
        
        // If we have both from metadata, use them but still verify with database
        if (role && tenant_id) {
          console.log('🔄 Attempting to fetch tenant plan...');
          
          // Set user immediately with starter plan, then update with real plan
          const tempUser = {
            ...authUser,
            email: authUser.email,
            role: role,
            tenant_id: tenant_id,
            plan: 'starter' // temporary fallback
          };
          
          console.log('✅ Setting temp user with metadata (starter plan):', { email: tempUser.email, role: tempUser.role, tenant_id: tempUser.tenant_id, plan: tempUser.plan });
if (mounted) {
  setUser(tempUser);
  userIdRef.current = tempUser.id; 
  setLoading(false);
  console.log('✅ Loading set to false after temp metadata user');
}
          
          // Fetch tenant plan in background with timeout
          const fetchTenantPlan = async () => {
            try {
              console.log('🔄 Background: fetching tenant plan...');
              
              // Add timeout to prevent hanging
              const timeoutPromise = new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Timeout')), 5000)
              );
              
              const fetchPromise = supabase
                .from('tenants')
                .select(TENANT_ACCOUNT_FIELDS)
                .eq('id', tenant_id)
                .single();

              const { data: tenant, error: tenantError } = await Promise.race([
                fetchPromise,
                timeoutPromise
              ]);

              if (tenantError) {
                console.warn('Background: Could not fetch tenant plan (error):', tenantError);
              } else if (['canceled', 'suspended'].includes(tenant?.subscription_status)) {
                console.warn(`🚫 Tenant subscription ${tenant.subscription_status} — signing out`);
                await supabase.auth.signOut();
                window.location.href = '/suspended';
                return;
              } else if (tenant) {
                // NOT gated on tenant.plan. These account-state fields drive the
                // paused-messaging and usage banners, and a tenant with a null plan
                // still needs them — gating here used to leave such an account
                // showing "messaging enabled" while its sending was hard-stopped.
                if (mounted) {
                  setUser(prev => ({
                    ...prev,
                    ...(tenant.plan ? { plan: tenant.plan } : {}),
                    ...accountStateFromTenant(tenant)
                  }));
                  console.log('✅ Background: Updated user with tenant account state:', tenant.plan || '(no plan)');
                }
                if (!tenant.plan) console.warn('Background: No tenant plan found, keeping starter');
              } else {
                console.warn('Background: No tenant row found, keeping starter');
              }
            } catch (error) {
              console.warn('Background: Could not fetch tenant plan (catch):', error.message);
              // Keep using starter plan
            }
          };
          
          // Don't await this - let it run in background
          fetchTenantPlan();
          
          // Optionally verify in background (don't wait for this)
          verifyUserProfile(authUser.id, role, tenant_id);
          return;
        }
        
        // If metadata is missing, get from database
        console.log('🔍 Fetching from users_profile table...');
        const { data: profile, error: profileError } = await supabase
          .from('users_profile')
          .select('role, tenant_id, terms_agreed_at, terms_notice_message, responsible_use_agreed_at')
          .eq('id', authUser.id)
          .single();

        // Get tenant plan if we have a tenant_id
        let tenantPlan = 'starter'; // default fallback
        let tenantAccountState = accountStateFromTenant(null);
        if (profile?.tenant_id || tenant_id) {
          try {
            const { data: tenant, error: tenantError } = await supabase
              .from('tenants')
              .select(TENANT_ACCOUNT_FIELDS)
              .eq('id', profile?.tenant_id || tenant_id)
              .single();

            if (tenantError) {
              console.warn('Could not fetch tenant plan (profile path):', tenantError);
            } else if (['canceled', 'suspended'].includes(tenant?.subscription_status)) {
              console.warn(`🚫 Tenant subscription ${tenant.subscription_status} — signing out`);
              await supabase.auth.signOut();
              window.location.href = '/suspended';
              return;
            } else if (tenant) {
              // Account state is read regardless of plan — see the background path.
              if (tenant.plan) tenantPlan = tenant.plan;
              tenantAccountState = accountStateFromTenant(tenant);
              console.log('✅ Got tenant plan (profile path):', tenantPlan);
            }
          } catch (error) {
            console.warn('Could not fetch tenant plan (profile catch):', error);
          }
        }

        if (profileError) {
          console.error('❌ Profile fetch error:', profileError);
          
          // Fallback: If profile doesn't exist, assume it's a new business admin
          console.log('⚡ Using fallback: business_admin role');
          const fallbackUser = {
            ...authUser,
            email: authUser.email,
            role: 'user',
            tenant_id: authUser.id,
            plan: tenantPlan
          };
          
          console.log('✅ Setting fallback user:', { email: fallbackUser.email, role: fallbackUser.role, tenant_id: fallbackUser.tenant_id, plan: fallbackUser.plan });
if (mounted) {
  setUser(fallbackUser);
  userIdRef.current = fallbackUser.id; 
  setLoading(false);
  console.log('✅ Loading set to false after fallback user');
}
          return;
        }

        if (profile) {
          console.log('✅ Profile loaded:', profile);
          const enrichedUser = {
            ...authUser,
            email: authUser.email,
            role: profile.role || 'business_admin',
            tenant_id: profile.tenant_id || authUser.id,
            plan: tenantPlan,
            ...tenantAccountState,
            terms_agreed_at: profile.terms_agreed_at || null,
            terms_notice_message: profile.terms_notice_message || null,
            responsible_use_agreed_at: profile.responsible_use_agreed_at || null
          };
          
          console.log('✅ Setting user with profile data:', { email: enrichedUser.email, role: enrichedUser.role, tenant_id: enrichedUser.tenant_id, plan: enrichedUser.plan });
if (mounted) {
  setUser(enrichedUser);
  userIdRef.current = enrichedUser.id; 
  setLoading(false);
  console.log('✅ Loading set to false after profile user');
}
          
          // Update auth metadata if it's missing - UPDATE BOTH user_metadata AND app_metadata
          if (!role || !tenant_id) {
            updateAuthMetadata(authUser.id, profile.role, profile.tenant_id);
          }
        }
        
      } catch (error) {
        console.error('❌ Error loading user info:', error);
        
        // Ultimate fallback
        const ultimateFallbackUser = {
          ...authUser,
          email: authUser.email,
          role: 'user',
          tenant_id: authUser.id,
          plan: 'starter'
        };
        
        console.log('✅ Setting ultimate fallback user:', { email: ultimateFallbackUser.email, role: ultimateFallbackUser.role, tenant_id: ultimateFallbackUser.tenant_id, plan: ultimateFallbackUser.plan });
if (mounted) {
  setUser(ultimateFallbackUser);
  userIdRef.current = ultimateFallbackUser.id; 
  setLoading(false);
  console.log('✅ Loading set to false after ultimate fallback user');
}
      }
    };

    // Background verification of user profile
    const verifyUserProfile = async (userId, currentRole, currentTenantId) => {
      try {
        const { data: profile } = await supabase
          .from('users_profile')
          .select('role, tenant_id, terms_agreed_at, terms_notice_message, responsible_use_agreed_at')
          .eq('id', userId)
          .single();

        // If database differs from metadata, update user state
        if (profile && (profile.role !== currentRole || profile.tenant_id !== currentTenantId)) {
          console.log('🔄 Profile changed, updating user...');

          // Get updated tenant plan
          let tenantPlan = 'starter';
          try {
            const { data: tenant } = await supabase
              .from('tenants')
              .select('plan')
              .eq('id', profile.tenant_id)
              .single();

            if (tenant?.plan) {
              tenantPlan = tenant.plan;
            }
          } catch (error) {
            console.warn('Could not fetch updated tenant plan:', error);
          }

          if (mounted) {
            setUser(prev => ({
              ...prev,
              role: profile.role,
              tenant_id: profile.tenant_id,
              plan: tenantPlan,
              terms_agreed_at: profile.terms_agreed_at || null,
              terms_notice_message: profile.terms_notice_message || null,
              responsible_use_agreed_at: profile.responsible_use_agreed_at || null
            }));
          }
        } else if (profile) {
          // Even if role/tenant hasn't changed, update terms_agreed_at
          if (mounted) {
            setUser(prev => ({
              ...prev,
              terms_agreed_at: profile.terms_agreed_at || null,
              terms_notice_message: profile.terms_notice_message || null,
              responsible_use_agreed_at: profile.responsible_use_agreed_at || null
            }));
          }
        }
      } catch (error) {
        console.log('⚠️ Background verification failed:', error);
      }
    };

    // Update auth metadata to sync with database - ENHANCED VERSION
    const updateAuthMetadata = async (userId, role, tenantId) => {
      try {
        console.log('🔄 Updating auth metadata for user:', userId, 'role:', role, 'tenant_id:', tenantId);
        
        // Update user metadata (this gets included in JWT more reliably)
        await supabase.auth.updateUser({
          data: { 
            role: role, 
            tenant_id: tenantId 
          }
        });
        console.log('✅ User metadata updated');

        // Also update via SQL to ensure both app_metadata and user_metadata are set
        const { error: sqlError } = await supabase.rpc('update_user_metadata', {
          user_id: userId,
          new_role: role,
          new_tenant_id: tenantId
        });

        if (sqlError) {
          console.warn('⚠️ SQL metadata update failed:', sqlError.message);
          // Not critical, continue with normal flow
        } else {
          console.log('✅ SQL metadata update successful');
        }

        // 🔄 Force session refresh to apply new metadata to JWT
        const { data: refreshedSession, error: refreshError } = await supabase.auth.refreshSession();
        if (refreshedSession?.session && mounted) {
          console.log('🔁 Session refreshed to reflect new metadata');
          setSession(refreshedSession.session);
          localStorage.setItem('auth_token', refreshedSession.session.access_token);
          
          // Debug the refreshed JWT
          if (refreshedSession.session.access_token) {
            try {
              const payload = JSON.parse(atob(refreshedSession.session.access_token.split('.')[1]));
              console.log('🔍 Refreshed JWT payload:', payload);
              console.log('🔍 Refreshed tenant_id in JWT:', payload.tenant_id);
            } catch (e) {
              console.warn('Could not decode refreshed JWT:', e);
            }
          }
          
          await loadUserInfo(refreshedSession.session.user); // rehydrate user state
        } else {
          console.warn('⚠️ Session refresh failed after metadata update:', refreshError?.message);
        }

      } catch (error) {
        console.log('⚠️ Failed to update auth metadata:', error);
      }
    };

    // Initialize immediately
    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      if (!mounted) return;
      
      console.log('🔄 Auth event:', event);
      
if (event === 'SIGNED_OUT') {
  // Close any active sessions before clearing state
  const userId = userIdRef.current || user?.id;
  
  if (userId) {
    console.log('🔚 Closing sessions for user:', userId);
    supabase
      .from('user_sessions')
      .update({
        session_end: new Date().toISOString(),
        last_activity: new Date().toISOString()
      })
      .eq('user_id', userId)
      .is('session_end', null)
      .then(({ error }) => {
        if (error) {
          console.error('❌ Error closing sessions on logout:', error);
        } else {
          console.log('✅ Sessions closed on logout');
        }
      });
  } else {
    console.warn('⚠️ No user ID available to close sessions');
  }
  
  setUser(null);
  setSession(null);
  setLoading(false);
  localStorage.removeItem('auth_token');
  userIdRef.current = null;
} else if (event === 'TOKEN_REFRESHED') {
        console.log('🔄 Token refreshed');
        setSession(newSession);
        if (newSession?.access_token) {
          localStorage.setItem('auth_token', newSession.access_token);

          // Debug refreshed token
          try {
            const payload = JSON.parse(atob(newSession.access_token.split('.')[1]));
            console.log('🔍 Token refresh - JWT payload:', payload);
            console.log('🔍 Token refresh - tenant_id in JWT:', payload.tenant_id);
          } catch (e) {
            console.warn('Could not decode refreshed JWT:', e);
          }
        }
      } else if (event === 'INITIAL_SESSION' && newSession?.user) {
        console.log('🔗 Initial session established from URL tokens');
        setSession(newSession);

        // Store token in localStorage for backend API calls
        if (newSession.access_token) {
          localStorage.setItem('auth_token', newSession.access_token);
        }

        // Load user info dynamically
        await loadUserInfo(newSession.user);

        // Only log a login_success audit entry when this INITIAL_SESSION was triggered
        // by real URL auth tokens (magic link, email confirmation, invitation).
        // Regular page loads/tab focuses where the user was already authenticated also
        // fire INITIAL_SESSION — those are token refreshes, not logins, so skip them.
        if (hasAuthTokensOnLoad) {
          const flowType = sessionStorage.getItem('auth_flow_type') || 'email_confirmation';
          sessionStorage.removeItem('auth_flow_type');
          fetch('/api/auth/log-session-start', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${newSession.access_token}`,
            },
            body: JSON.stringify({ source: flowType }),
          }).catch(() => {}); // Non-blocking — auth flow must not depend on audit logging
        }
      } else if (newSession?.user) {
        setSession(newSession);

        // Store token in localStorage for backend API calls
        if (newSession.access_token) {
          localStorage.setItem('auth_token', newSession.access_token);
        }

        // Load user info dynamically
        await loadUserInfo(newSession.user);
      }
    });

    // Note: autoRefreshToken: true in supabaseClient handles token refresh automatically.
    // Do NOT add a manual periodic refreshSession() interval here — it races with the
    // SDK's internal refresh lock and triggers GoTrue's refresh token reuse detection,
    // which revokes the session and signs the user out.

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Helper to get current valid session.
  // Uses getSession() which goes through the SDK's internal auto-refresh lock,
  // preventing concurrent refreshSession() races that cause reuse detection + SIGNED_OUT.
  const getSession = async () => {
    const { data, error } = await supabase.auth.getSession();
    if (!error && data?.session) {
      return data.session;
    }
    return session;
  };

  // Helper to check enterprise access
  const canAccessEnterprise = user?.role === 'global_admin' || user?.role === 'partner_admin' || user?.role === 'co_admin';
  const canAccessAdmin = ['global_admin', 'enterprise_admin', 'business_admin', 'co_admin'].includes(user?.role);

  // NEW: Add partner network helpers
  const canAccessPartnerNetwork = ['global_admin', 'partner_admin', 'co_admin'].includes(user?.role);
  const canManageMultipleLocations = ['global_admin', 'partner_admin', 'co_admin'].includes(user?.role);

  const refreshUser = async () => {
    try {
      console.log('🔄 Refreshing user data...');
      const { data, error } = await supabase.auth.getUser();
      
      if (data?.user && !error) {
        // Re-fetch user profile from database
        const { data: profile, error: profileError } = await supabase
          .from('users_profile')
          .select('role, tenant_id, terms_agreed_at, terms_notice_message, responsible_use_agreed_at')
          .eq('id', data.user.id)
          .single();

        // Get tenant plan
        let tenantPlan = 'starter';
        if (profile?.tenant_id) {
          const { data: tenant } = await supabase
            .from('tenants')
            .select('plan')
            .eq('id', profile.tenant_id)
            .single();

          if (tenant?.plan) {
            tenantPlan = tenant.plan;
          }
        }

        if (profile && !profileError) {
          const refreshedUser = {
            ...data.user,
            email: data.user.email,
            role: profile.role || 'business_admin',
            tenant_id: profile.tenant_id || data.user.id,
            plan: tenantPlan,
            terms_agreed_at: profile.terms_agreed_at || null,
            terms_notice_message: profile.terms_notice_message || null,
            responsible_use_agreed_at: profile.responsible_use_agreed_at || null
          };

          console.log('✅ User refreshed:', { email: refreshedUser.email, role: refreshedUser.role, tenant_id: refreshedUser.tenant_id, plan: refreshedUser.plan });
          setUser(refreshedUser);
        } else {
          console.log('⚠️ Profile refresh failed, using fallback');
          const fallbackRefreshUser = {
            ...data.user,
            email: data.user.email,
            role: 'user',
            tenant_id: data.user.id,
            plan: tenantPlan
          };
          
          console.log('✅ Fallback user set:', { email: fallbackRefreshUser.email, role: fallbackRefreshUser.role, tenant_id: fallbackRefreshUser.tenant_id, plan: fallbackRefreshUser.plan });
          setUser(fallbackRefreshUser);
        }
      }
    } catch (error) {
      console.error('❌ Error refreshing user:', error);
    }
  };

  // Add debugging log for user state changes
  useEffect(() => {
    if (user) {
      console.log('👤 User state updated:', { 
        email: user.email, 
        role: user.role, 
        tenant_id: user.tenant_id,
        plan: user.plan,
        hasAllRequiredFields: !!(user.email && user.role && user.tenant_id)
      });
    } else {
      console.log('👤 User state is null/undefined');
    }
  }, [user]);

  // Track user ID in ref for logout
  useEffect(() => {
    if (user?.id) {
      userIdRef.current = user.id;
    } else {
      userIdRef.current = null;
    }
  }, [user?.id]);

  // NEW: Add these helper functions (they work even if plan/permission data isn't loaded yet)
  const hasPermission = (permission) => {
    if (!user?.role) return false;
    try {
      return checkPermission(user.role, permission);
    } catch (error) {
      console.warn('Permission check failed:', error);
      return false;
    }
  };

  const hasFeature = (feature) => {
    try {
      // Use the user's actual plan, fallback to 'starter' if not available
      const plan = user?.plan || 'starter';
      return checkFeature(plan, feature);
    } catch (error) {
      console.warn('Feature check failed:', error);
      return false;
    }
  };

  const getCurrentPlan = () => {
    return user?.plan || 'starter';
  };

  // Review sandbox: several code paths rebuild `user` WITHOUT the tenant account fields (refreshUser, the fallback
  // users, the fast-path temp user before the tenant row loads), which silently dropped `is_reviewer` and made the
  // reviewer UI (sandbox banner, docs panel, Terms exemption) vanish and the A2P banner / Go Live widget reappear.
  // The flag is stamped into the login's app_metadata at creation (users cannot edit app_metadata), so it is read
  // from there on EVERY build. Only ever turns the flag on; never touches other users.
  const userWithFlags = React.useMemo(() => {
    if (!user) return user;
    let u = user;
    if (u.is_reviewer !== true && u.app_metadata?.is_reviewer === true) u = { ...u, is_reviewer: true };
    // Same reason as is_reviewer above: also stamped into app_metadata at creation, so it survives every rebuild of `user`.
    if (u.is_prospect_demo !== true && u.app_metadata?.reviewer_kind === 'prospect_demo') u = { ...u, is_prospect_demo: true };
    return u;
  }, [user]);

  // ONLY EXTEND the existing value object - don't change anything else
  const value = {
    user: userWithFlags,
    loading,
    session,
    getSession,
    role: user?.role,
    tenantId: user?.tenant_id,
    canAccessEnterprise,
    canAccessAdmin,
    canAccessPartnerNetwork,
    canManageMultipleLocations,
    refreshUser,
    
    // NEW: Add these minimal helpers
    hasPermission,
    hasFeature,
    currentPlan: getCurrentPlan(),
    permissions: user?.role ? getRolePermissions(user.role) : []
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

// Create useSessionTracking hook
// COMPLETE FIXED VERSION - Replace your useSessionTracking hook with this

// COMPLETE FIXED VERSION - Replace your useSessionTracking hook with this

const useSessionTracking = () => {
  const { user, session } = useAuth();
  const [sessionId, setSessionId] = useState(null);
  const sessionIdRef = useRef(null);
  const activityIntervalRef = useRef(null);
  const isInitializedRef = useRef(false);
  
  // Main effect - Initialize or resume session
  useEffect(() => {
    // Only proceed if we have a valid authenticated user with all required fields
    if (!user?.id || !user?.tenant_id || !session?.access_token) {
      console.log('⏭️ Session tracking waiting for user data...');
      return;
    }
    
    // Prevent multiple initializations
    if (isInitializedRef.current) {
      return;
    }
    
    isInitializedRef.current = true;
    let mounted = true;
    
    const initializeSession = async () => {
      try {
        console.log('🔍 Initializing session for user:', user.id);
        
        // Check for existing active session
        const { data: existingSessions, error: queryError } = await supabase
          .from('user_sessions')
          .select('id, session_start, last_activity')
          .eq('user_id', user.id)
          .is('session_end', null)
          .order('session_start', { ascending: false })
          .limit(1);
        
        if (queryError) {
          console.error('❌ Session query error:', queryError);
          return;
        }
        
        let currentSessionId = null;
        
        if (existingSessions?.[0]) {
          // Resume existing session
          currentSessionId = existingSessions[0].id;
          console.log('✅ Resuming session:', currentSessionId);
          
          // Update last_activity immediately
          await supabase
            .from('user_sessions')
            .update({
              last_activity: new Date().toISOString()
            })
            .eq('id', currentSessionId);
            
        } else {
          // Create new session
          console.log('🆕 Creating new session...');
          
          const { data: newSession, error: createError } = await supabase
            .from('user_sessions')
            .insert({
              user_id: user.id,
              tenant_id: user.tenant_id,
              login_method: 'email',
              user_agent: navigator.userAgent,
              device_type: /Mobile|Android|iPhone|iPad/.test(navigator.userAgent) ? 'mobile' : 'desktop',
              pages_visited: 1,
              actions_performed: 0,
              last_activity: new Date().toISOString()
            })
            .select('id')
            .single();
            
          if (createError) {
            console.error('❌ Session creation error:', createError);
            return;
          }
          
          currentSessionId = newSession.id;
          console.log('✅ Created session:', currentSessionId);
        }
        
        if (mounted && currentSessionId) {
          setSessionId(currentSessionId);
          sessionIdRef.current = currentSessionId;
          
          // Start activity tracking and store cleanup function
          const cleanupActivityListeners = startActivityTracking(currentSessionId);
          
          // Store cleanup function for later
          return cleanupActivityListeners;
        }
        
      } catch (error) {
        console.error('❌ Session initialization error:', error);
      }
    };
    
    const startActivityTracking = (sessionId) => {
      let lastInteractionTime = Date.now();
      
      // Track real user interactions
      const trackInteraction = () => {
        lastInteractionTime = Date.now();
      };
      
      // Listen for actual user activity
      const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
      activityEvents.forEach(event => {
        document.addEventListener(event, trackInteraction, { passive: true });
      });
      
      // Clear any existing interval
      if (activityIntervalRef.current) {
        clearInterval(activityIntervalRef.current);
      }
      
      // Check activity every 2 minutes
      activityIntervalRef.current = setInterval(async () => {
        if (!sessionId) return;
        
        // Only update if user was active in last 2 minutes
        const minutesSinceInteraction = (Date.now() - lastInteractionTime) / 1000 / 60;
        
        if (minutesSinceInteraction < 2) {
          try {
            console.log('📊 Updating activity for session:', sessionId);
            
            const { error } = await supabase
              .from('user_sessions')
              .update({
                last_activity: new Date().toISOString()
              })
              .eq('id', sessionId);
              
            if (error) {
              console.error('❌ Activity update error:', error);
            } else {
              console.log('✅ Activity updated successfully');
            }
          } catch (error) {
            console.error('❌ Activity tracking error:', error);
          }
        } else {
          console.log('⏭️ No user interaction, skipping activity update');
        }
      }, 2 * 60 * 1000); // 2 minutes
      
      console.log('⏰ Activity tracking started');
      
      // Store event listeners for cleanup
      return () => {
        activityEvents.forEach(event => {
          document.removeEventListener(event, trackInteraction);
        });
      };
    };
    
    initializeSession();
    
    // Handle visibility change (when user switches tabs or closes)
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'hidden' && sessionIdRef.current) {
        // User switched away or closed tab - update last_activity
        try {
          await supabase
            .from('user_sessions')
            .update({
              last_activity: new Date().toISOString()
            })
            .eq('id', sessionIdRef.current);
        } catch (error) {
          console.error('Error updating on visibility change:', error);
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      mounted = false;
      isInitializedRef.current = false;
      
      // Clear activity interval
      if (activityIntervalRef.current) {
        clearInterval(activityIntervalRef.current);
        activityIntervalRef.current = null;
      }
      
      // Remove event listener
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      // Close session (for React navigation, not browser close)
      if (sessionIdRef.current) {
        console.log('🔚 Closing session:', sessionIdRef.current);
        supabase
          .from('user_sessions')
          .update({
            session_end: new Date().toISOString(),
            last_activity: new Date().toISOString()
          })
          .eq('id', sessionIdRef.current)
          .then(() => console.log('✅ Session closed'))
          .catch(err => console.error('❌ Session close error:', err));
      }
    };
  // Intentionally omit session?.access_token — Supabase refreshes the JWT on tab
  // focus which changes the token but should NOT restart session tracking (that
  // would close the DB session, reset the guard, and cascade errors into any open
  // modal or form the user was filling out).
  }, [user?.id, user?.tenant_id]); // eslint-disable-line react-hooks/exhaustive-deps
  
  return { sessionId };
};

export { AuthProvider, useAuth, useSessionTracking };